/**
 * Finds the first valid discount from a list of threshold discounts based on expiration criteria.
 *
 * This function filters through threshold discounts and returns the first discount that is still valid.
 * A discount is considered valid if:
 * - It has no expiration date (never expires)
 * - It has an expiration date that is more than 2 days in the future (effective expiration)
 *
 * The function uses an "effective expiration date" (2 days before the actual expiration) to avoid
 * showing users discounts that are about to expire.
 *
 * @param {Object} discountsData - The discounts data object
 * @param {Array} [discountsData.thresholdDiscounts] - Array of discount objects to filter through
 * @returns {Object|undefined} The first valid discount object, or undefined if no valid discounts are found
 *
 * @example
 * // Example discountsData structure:
 * const discountsData = {
 *   thresholdDiscounts: [
 *     {
 *       id: 1,
 *       expiresOn: { date: '2024-01-15T12:00:00.000Z' },
 *       // ... other discount properties
 *     },
 *     {
 *       id: 2,
 *       // No expiresOn - never expires
 *       // ... other discount properties
 *     }
 *   ]
 * };
 *
 * const validDiscount = getSelectedDiscount(discountsData);
 * // Returns the first discount that hasn't expired (with 2-day grace period)
 */
function getSelectedDiscount(discountsData) {
    return discountsData?.thresholdDiscounts?.find((discount) => {
        // If no expiresOn date, consider it valid
        if (!discount?.expiresOn?.date) {
            return true;
        }

        // Parse the expiration date as UTC
        // Convert date string from 'YYYY-MM-DD' format to ISO format by:
        // 1. Replacing any space with 'T' to separate date from time
        // 2. Adding 'Z' suffix to indicate UTC timezone
        // This ensures consistent date parsing regardless of local timezone
        const expirationDate = new Date(`${discount.expiresOn.date.replace(' ', 'T')}Z`);
        if (!expirationDate) {
            return true;
        }

        // Calculate the effective expiration date (2 days before the actual expiration)
        const effectiveExpirationDate = new Date(expirationDate.getTime());
        effectiveExpirationDate.setDate(effectiveExpirationDate.getDate() - 2);

        // Check if the current date is before the effective expiration date
        const currentDate = new Date();
        return currentDate < effectiveExpirationDate;
    });
}

export { getSelectedDiscount };
